import { useCallback, useMemo, useRef, useState } from 'react';
import { ChatUploadResponse, deleteChatUpload, registerHubDocumentForChat } from '../api';
import { IMAGE_MIME_TYPES } from 'src/features/uploads/utils';
import { useHubUpload } from 'src/features/uploads/useHubUpload';

export const GEMINI_CONTEXT_LIMIT = 800_000;

export type UploadStatus = 'uploading' | 'scanning' | 'processing' | 'complete' | 'error';

export interface FileUpload {
  file: File;
  status: UploadStatus;
  /** Upload progress percentage (0–100). Only meaningful when status is 'uploading'. */
  uploadProgress: number;
  fileId?: string;
  /** Document ULID (non-image uploads only). */
  documentId?: string;
  tokenCount?: number;
  isExact?: boolean;
  error?: string;
  /** Blob URL for image previews. Only set for image files. */
  previewUrl?: string;
  /** Server preview (JPEG rendition) tried when the local previewUrl can't decode (HEIC in Chrome). */
  fallbackPreviewUrl?: string;
}

/** Result emitted by ``waitForUploads`` for each completed upload. */
export interface CompletedUpload {
  file_id: string;
  /** Document ULID — present for parsed documents, absent for images. */
  document_id?: string;
  filename: string;
  /** Uploads Map key — stable per-upload id, distinguishing same-named files. */
  upload_key: string;
  /** Browser-renderable preview (JPEG rendition) for a HEIC/HEIF image. */
  preview_url?: string;
}

interface UseChatFileUploadReturn {
  uploads: Map<string, FileUpload>;
  uploadFile: (file: File) => void;
  removeFile: (key: string) => void;
  clearUploads: (opts?: { revokePreviewUrls?: boolean; keepInflight?: boolean }) => void;
  /** Restore a previously saved uploads snapshot (no new network requests). */
  restoreUploads: (saved: Map<string, FileUpload>) => void;
  /** Get the latest uploads map (safe to call from async code where state may be stale). */
  getUploads: () => Map<string, FileUpload>;
  /** Ordered list of files derived from uploads. */
  files: File[];
  allUploadsComplete: boolean;
  hasUploadErrors: boolean;
  totalFileTokens: number;
  fileIds: string[];
  exceedsContextLimit: boolean;
  waitForUploads: () => Promise<CompletedUpload[]>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useChatFileUpload(): UseChatFileUploadReturn {
  // Chat-specific per-file data returned by registerHubDocumentForChat
  const registrationsRef = useRef<Map<string, ChatUploadResponse>>(new Map());
  const completedUploadsRef = useRef<CompletedUpload[]>([]);

  const hub = useHubUpload({
    documentType: 'chat_upload',
    skipDI: (_file, ct) => IMAGE_MIME_TYPES.has(ct) || IMAGE_MIME_TYPES.has(_file.type),
    onFileReady: async (documentId, file, key) => {
      const result = await registerHubDocumentForChat(documentId);
      registrationsRef.current.set(key, result);
      completedUploadsRef.current = [
        ...completedUploadsRef.current.filter((upload) => upload.upload_key !== key),
        {
          file_id: result.file_id,
          document_id: result.document_id,
          filename: file.name,
          upload_key: key,
          preview_url: result.preview_url
        }
      ];
    }
  });

  // Derive FileUpload (chat-facing shape) from hub state + registrations.
  // When hub.uploads changes (stage transitions), useMemo re-runs and reads
  // the latest registrationsRef.current — which is set synchronously before
  // hub patchFile('ready') triggers the re-render.
  const derivedUploads = useMemo<Map<string, FileUpload>>(() => {
    const result = new Map<string, FileUpload>();
    for (const [key, hs] of hub.uploads) {
      const reg = registrationsRef.current.get(key);
      result.set(key, {
        file: hs.file,
        status: hs.stage === 'ready' ? 'complete' : hs.stage,
        uploadProgress: hs.uploadProgress,
        fileId: reg?.file_id,
        documentId: reg?.document_id ?? hs.documentId,
        tokenCount: reg?.token_count,
        isExact: reg?.is_exact,
        previewUrl: hs.previewUrl,
        fallbackPreviewUrl: reg?.preview_url,
        error: hs.error
      });
    }
    return result;
  }, [hub.uploads]);

  // Allow restoring a snapshot without going through hub (e.g. navigating back
  // to a previous chat message). Cleared as soon as a new file is uploaded.
  const [restoredUploads, setRestoredUploads] = useState<Map<string, FileUpload> | null>(null);

  const uploads = restoredUploads ?? derivedUploads;

  // Keep a ref of uploads for async code (getUploads / waitForUploads)
  const uploadsRef = useRef<Map<string, FileUpload>>(uploads);
  uploadsRef.current = uploads;

  const uploadFile = useCallback(
    (file: File) => {
      setRestoredUploads(null); // clear any restored snapshot
      hub.uploadFiles([file]);
    },
    [hub]
  );

  const removeFile = useCallback(
    (key: string) => {
      const upload = uploadsRef.current.get(key);
      // Delete from the chat backend if already registered
      if (upload?.status === 'complete' && upload.fileId) {
        deleteChatUpload(upload.fileId);
      }
      registrationsRef.current.delete(key);
      hub.removeFile(key);
    },
    [hub]
  );

  const clearUploads = useCallback(
    ({
      revokePreviewUrls = true,
      keepInflight = false
    }: { revokePreviewUrls?: boolean; keepInflight?: boolean } = {}) => {
      setRestoredUploads(null);
      if (!keepInflight) {
        registrationsRef.current.clear();
        completedUploadsRef.current = [];
      }
      hub.clearUploads({ revokePreviewUrls, keepInflight });
    },
    [hub]
  );

  const restoreUploads = useCallback((saved: Map<string, FileUpload>) => {
    setRestoredUploads(new Map(saved));
    completedUploadsRef.current = Array.from(saved.entries())
      .filter(([, u]) => u.status === 'complete' && u.fileId)
      .map(([key, u]) => ({
        file_id: u.fileId!,
        document_id: u.documentId,
        filename: u.file.name,
        upload_key: key,
        preview_url: u.fallbackPreviewUrl
      }));
  }, []);

  const getUploads = useCallback(() => uploadsRef.current, []);

  const waitForUploads = useCallback(async (): Promise<CompletedUpload[]> => {
    await hub.waitForAll();
    return [...completedUploadsRef.current];
  }, [hub]);

  const files = useMemo(() => Array.from(uploads.values()).map((u) => u.file), [uploads]);

  const allUploadsComplete = useMemo(
    () => uploads.size > 0 && Array.from(uploads.values()).every((u) => u.status === 'complete'),
    [uploads]
  );

  const hasUploadErrors = useMemo(() => Array.from(uploads.values()).some((u) => u.status === 'error'), [uploads]);

  const totalFileTokens = useMemo(
    () => Array.from(uploads.values()).reduce((sum, u) => sum + (u.tokenCount ?? 0), 0),
    [uploads]
  );

  const fileIds = useMemo(
    () =>
      Array.from(uploads.values())
        .filter((u) => u.status === 'complete' && u.fileId)
        .map((u) => u.fileId!),
    [uploads]
  );

  const exceedsContextLimit = totalFileTokens > GEMINI_CONTEXT_LIMIT;

  return {
    uploads,
    uploadFile,
    removeFile,
    clearUploads,
    restoreUploads,
    getUploads,
    files,
    allUploadsComplete,
    hasUploadErrors,
    totalFileTokens,
    fileIds,
    exceedsContextLimit,
    waitForUploads
  };
}
